/**
 * The route table.
 */
import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { DocumentScreen } from '@features/documents/DocumentScreen';
import { PartyScreen } from '@features/parties/PartyScreen';
import { ProductScreen } from '@features/products/ProductScreen';
import { BusinessProfileScreen } from '@features/settings/BusinessProfileScreen';
import { BackupRestoreScreen } from '@features/settings/BackupRestoreScreen';
import { SyncSettingsScreen } from '@features/settings/SyncSettingsScreen';
import { CreateInvoiceScreen } from '@features/invoices/CreateInvoiceScreen';
import { ReceiptsScreen } from '@features/payments/ReceiptsScreen';
import { QuotationListScreen } from '@features/quotations/QuotationListScreen';
import { QuotationEditorScreen } from '@features/quotations/QuotationEditorScreen';
import { QuotationDocumentScreen } from '@features/quotations/QuotationDocumentScreen';
import { SalesOrderListScreen } from '@features/salesOrders/SalesOrderListScreen';
import { SalesOrderEditorScreen } from '@features/salesOrders/SalesOrderEditorScreen';
import { SalesOrderDocumentScreen } from '@features/salesOrders/SalesOrderDocumentScreen';
import { PurchaseOrderListScreen } from '@features/purchaseOrders/PurchaseOrderListScreen';
import { PurchaseOrderEditorScreen } from '@features/purchaseOrders/PurchaseOrderEditorScreen';
import { PurchaseOrderDocumentScreen } from '@features/purchaseOrders/PurchaseOrderDocumentScreen';
import { DeliveryNoteListScreen } from '@features/deliveryNotes/DeliveryNoteListScreen';
import { DeliveryNoteEditorScreen } from '@features/deliveryNotes/DeliveryNoteEditorScreen';
import { DeliveryNoteDocumentScreen } from '@features/deliveryNotes/DeliveryNoteDocumentScreen';
import { NewReturnScreen, ReturnDocumentScreen, ReturnEditorScreen, ReturnListScreen } from '@features/creditNotes/screens';
import { NotImplementedState } from '@ui/components/States';
import { AppShell } from '../nav/AppShell';
import { DEFAULT_ROUTE } from '../nav/navigation';
import { matchRoute, type Route } from './matchRoute';
import { MarketingPage } from '@features/marketing/MarketingPage';
import { SyncCoordinator } from './SyncCoordinator';
import { HelpScreen } from '@features/help/HelpScreen';

function currentRoute(): string {
  return window.location.hash || DEFAULT_ROUTE;
}

function useHashRoute(): string {
  const [hash, setHash] = useState(currentRoute);
  useEffect(() => {
    const onChange = () => {
      flushSync(() => setHash(currentRoute()));
    };
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return hash;
}

function screenFor(route: Route) {
  switch (route.kind) {
    case 'new-invoice':
      return <CreateInvoiceScreen />;
    case 'edit-invoice':
      return <CreateInvoiceScreen documentId={route.documentId} />;
    case 'documents':
      return <DocumentScreen documentId={null} />;
    case 'quotations':
      return <QuotationListScreen />;
    case 'new-quotation':
      return <QuotationEditorScreen />;
    case 'edit-quotation':
      return <QuotationEditorScreen documentId={route.documentId} />;
    case 'quotation':
      return <QuotationDocumentScreen documentId={route.documentId} />;
    case 'sales-orders':
      return <SalesOrderListScreen />;
    case 'new-sales-order':
      return <SalesOrderEditorScreen />;
    case 'edit-sales-order':
      return <SalesOrderEditorScreen documentId={route.documentId} />;
    case 'sales-order':
      return <SalesOrderDocumentScreen documentId={route.documentId} />;
    case 'purchase-orders':
      return <PurchaseOrderListScreen />;
    case 'new-purchase-order':
      return <PurchaseOrderEditorScreen />;
    case 'edit-purchase-order':
      return <PurchaseOrderEditorScreen documentId={route.documentId} />;
    case 'purchase-order':
      return <PurchaseOrderDocumentScreen documentId={route.documentId} />;
    case 'delivery-notes':
      return <DeliveryNoteListScreen />;
    case 'new-delivery-note':
      return <DeliveryNoteEditorScreen salesOrderId={route.salesOrderId} />;
    case 'edit-delivery-note':
      return <DeliveryNoteEditorScreen documentId={route.documentId} />;
    case 'delivery-note':
      return <DeliveryNoteDocumentScreen documentId={route.documentId} />;
    case 'credit-notes':
      return <ReturnListScreen docType="CREDIT_NOTE" />;
    case 'new-return':
      return <NewReturnScreen docType={route.docType} invoiceId={route.invoiceId} />;
    case 'credit-note':
      return <ReturnDocumentScreen documentId={route.documentId} docType="CREDIT_NOTE" />;
    case 'edit-credit-note':
      return <ReturnEditorScreen docType="CREDIT_NOTE" documentId={route.documentId} />;
    case 'sales-returns':
      return <ReturnListScreen docType="SALES_RETURN" />;
    case 'sales-return':
      return <ReturnDocumentScreen documentId={route.documentId} docType="SALES_RETURN" />;
    case 'edit-sales-return':
      return <ReturnEditorScreen docType="SALES_RETURN" documentId={route.documentId} />;
    case 'document':
      return <DocumentScreen documentId={route.documentId} />;
    case 'contacts':
      return <PartyScreen initialKind={route.partyKind} />;
    case 'receipts':
      return <ReceiptsScreen />;
    case 'products':
      return <ProductScreen />;
    case 'backup':
      return <BackupRestoreScreen />;
    case 'sync':
      return <SyncSettingsScreen />;
    case 'business-profile':
      return <BusinessProfileScreen />;
    case 'help':
      return <HelpScreen />;
    case 'marketing':
      return <MarketingPage />;
    case 'planned':
      return <NotImplementedState title={route.navItem.label} group={route.navItem.group} />;
    default:
      return (
        <div className='p-6'>
          <h1 className='mb-2 text-lg font-semibold text-slate-100'>No such page</h1>
          <p className='text-sm text-slate-400'>
            <code className='rounded bg-slate-800 px-1.5 py-0.5 text-xs'>{route.route}</code> does not match anything in this app.
          </p>
          <p className='mt-3 text-sm'>
            <a href={DEFAULT_ROUTE} className='text-brand-500 underline underline-offset-2'>
              Go to invoices
            </a>
          </p>
        </div>
      );
  }
}

export function Router() {
  const route = useHashRoute();
  return (
    <>
      <SyncCoordinator />
      <AppShell route={route}>{screenFor(matchRoute(route))}</AppShell>
    </>
  );
}